import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { 
  FaShieldAlt, 
  FaSearch, 
  FaTerminal, 
  FaLock, 
  FaRocket, 
  FaCode, 
  FaCloud, 
  FaBolt, 
  FaTwitter, 
  FaLinkedin,
  FaDownload,
  FaUsers,
  FaChartLine,
  FaArrowRight,
  FaCheckCircle,
  FaLightbulb,
  FaGlobe,
  FaTimes,
  FaCookieBite,
  FaToggleOn,
  FaToggleOff,
  FaWindows,
  FaLinux,
  FaApple,
  FaPaperPlane,
  FaPlus,
  FaComments,
  FaRedo,
  FaCopy,
  FaThumbsUp,
  FaThumbsDown,
  FaUser,
  FaBug,
  FaDesktop,
  FaNetworkWired,
  FaMicroscope,
  FaDatabase,
  FaCogs,
  FaSearchPlus,
  FaServer
} from "react-icons/fa";
import Logo from "../components/Logo";
// Lazy load heavy animation components for better initial load performance
const ProductFlowAnimation = lazy(() => import("../components/ProductFlowAnimation"));
const VirusAnimation = lazy(() => import("../components/VirusAnimation"));
const NetworkPacketsAnimation = lazy(() => import("../components/NetworkPacketsAnimation"));
// AsciiAnimation is above the fold, keep it eagerly loaded but optimized
import AsciiAnimation from "../components/AsciiAnimation";
import backgroundVideo from "../assets/hero-background.mp4";
import commandSequences from "../data/terminalSequences";
import { demoSuggestions, findDemoResponse, demoChatSessions, demoChatHistories } from "../data/demoResponses";
import "../styles/Home.css";

function Home() {
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: true,
    functional: true,
    advertising: false
  });
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [activeSecurityDomain, setActiveSecurityDomain] = useState('malware');

  // Security domains data
  const securityDomains = {
    malware: {
      id: 'malware',
      title: 'Malware Research',
      icon: FaMicroscope,
      description: 'Dive deep into binary analysis and system behavior monitoring. Triagz empowers security researchers to dissect suspicious executables, track malware activity in real-time, and uncover hidden threats with AI-assisted analysis.',
      videoUrl: 'https://downloads.triagz.com/landing-page/malware%20research%20final.mp4'
    },
    threatIntel: {
      id: 'threatIntel',
      title: 'Threat Intelligence',
      icon: FaDatabase,
      description: 'Your command center for threat intelligence. Triagz seamlessly integrates with VirusTotal, AbuseIPDB, ThreatFox, and more—bringing all your favorite threat intel platforms into a single, unified workspace without the multiple platform-hopping.',
      videoUrl: 'https://downloads.triagz.com/landing-page/threat%20intel%20final.mp4'
    },
    reverseEng: {
      id: 'reverseEng',
      title: 'Reverse Engineering & Debugging',
      icon: FaCogs,
      description: 'Skip years of learning assembly. Triagz makes reverse engineering and debugging accessible with AI-powered disassembly, instant code analysis, and natural language queries—turning complex debugging tasks into simple conversations.',
      videoUrl: 'https://downloads.triagz.com/landing-page/re%20and%20debugging%20final.mp4'
    },
    dfir: {
      id: 'dfir',
      title: 'DFIR',
      icon: FaSearchPlus,
      description: 'Accelerate incident response and forensic investigations. Automate evidence collection, analyze system artifacts, perform memory analysis, and build comprehensive investigation timelines—all through conversational AI interactions.',
      videoUrl: 'https://downloads.triagz.com/landing-page/dfir%20final.mp4'
    },
    endpoint: {
      id: 'endpoint',
      title: 'Endpoint Research',
      icon: FaServer,
      description: 'Full endpoint control at your fingertips. Execute commands, extract system information, monitor processes, analyze network connections, and perform comprehensive endpoint research using simple natural language queries.',
      videoUrl: 'https://downloads.triagz.com/landing-page/command%20execution%20final.mp4'
    }
  };

  // Terminal animation state
  const [terminalLines, setTerminalLines] = useState([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const terminalBodyRef = useRef(null);
  const timeoutsRef = useRef([]);
  const isMountedRef = useRef(true);

  // Demo Portal state
  const [showDemoSection, setShowDemoSection] = useState(false);
  const [demoChatMessages, setDemoChatMessages] = useState(() => {
    // Initialize with pre-populated histories
    return { ...demoChatHistories };
  });
  const [demoInputMessage, setDemoInputMessage] = useState('');
  const [demoActiveChatId, setDemoActiveChatId] = useState(1);
  const [showDemoSuggestions, setShowDemoSuggestions] = useState(false);
  const [isProcessingDemo, setIsProcessingDemo] = useState(false);
  const [quickActionSuggestions, setQuickActionSuggestions] = useState([]);
  const [dropdownSuggestions, setDropdownSuggestions] = useState([]);
  const demoMessagesEndRef = useRef(null);
  const demoMessagesContainerRef = useRef(null);
  const demoSectionRef = useRef(null);
  const demoSuggestionsRef = useRef(null);
  const demoInitializedRef = useRef(false);

  // Get current chat messages
  const demoMessages = demoChatMessages[demoActiveChatId] || [];

  // Terminal animation effect
  useEffect(() => {
    let currentSequenceIndex = 0;
    isMountedRef.current = true;

    const runCommandSequence = async (sequence) => {
      if (!isMountedRef.current) return;
      
      setIsTyping(true);
      
      // Type the command
      const command = sequence.command;
      for (let i = 0; i <= command.length; i++) {
        if (!isMountedRef.current) return;
        
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            if (isMountedRef.current) {
              setCurrentCommand(command.substring(0, i));
            }
            resolve();
          }, 30);
          timeoutsRef.current.push(timeout);
        });
      }

      if (!isMountedRef.current) return;

      // Wait a bit after typing completes
      await new Promise(resolve => {
        const timeout = setTimeout(resolve, 500);
        timeoutsRef.current.push(timeout);
      });

      if (!isMountedRef.current) return;

      // Add the command to terminal lines
      setTerminalLines(prev => [...prev, { type: 'command', text: command }]);
      setCurrentCommand('');

      // Display responses with delays
      for (const response of sequence.responses) {
        if (!isMountedRef.current) return;
        
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            if (isMountedRef.current) {
              setTerminalLines(prev => [...prev, { type: 'response', text: response.text }]);
            }
            resolve();
          }, response.delay);
          timeoutsRef.current.push(timeout);
        });
      }

      if (!isMountedRef.current) return;

      setIsTyping(false);

      // Wait before next command
      await new Promise(resolve => {
        const timeout = setTimeout(resolve, 2000);
        timeoutsRef.current.push(timeout);
      });

      if (!isMountedRef.current) return;

      // Clear terminal after 3 commands
      setTerminalLines(prev => {
        if (prev.length > 20) {
          return [];
        }
        return prev;
      });

      // Move to next sequence
      currentSequenceIndex = (currentSequenceIndex + 1) % commandSequences.length;
      runCommandSequence(commandSequences[currentSequenceIndex]);
    };

    // Start the animation
    runCommandSequence(commandSequences[currentSequenceIndex]);

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, []); // Only run once on mount

  // Auto-scroll terminal to bottom
  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [terminalLines, currentCommand]);

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // Get the actual hero section element
      const heroSection = document.querySelector('.hero-section');
      
      if (heroSection) {
        // Get the bottom of the hero section
        const heroBottom = heroSection.offsetHeight - 100; // Subtract a bit for smooth transition
        
        // Add 'scrolled' class when user scrolls past hero section
        setIsHeaderScrolled(scrollPosition > heroBottom);
      } else {
        // Fallback: use viewport height if hero section not found
        setIsHeaderScrolled(scrollPosition > window.innerHeight * 0.9);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Check initial scroll position after a brief delay to ensure DOM is ready
    const timer = setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleCookieToggle = (category) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    setCookieSettings(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSaveCookies = () => {
    // Save cookie preferences to localStorage
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    setShowCookieModal(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      advertising: true
    };
    setCookieSettings(allAccepted);
    localStorage.setItem('cookieSettings', JSON.stringify(allAccepted));
    setShowCookieModal(false);
  };

  // Demo Portal Functions
  const handleTryDemo = () => {
    setShowDemoSection(true);
    // Scroll to demo section after a brief delay to ensure it's rendered
    setTimeout(() => {
      demoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollDemoToBottom = () => {
    // Scroll within the messages container, not the whole page
    if (demoMessagesContainerRef.current) {
      demoMessagesContainerRef.current.scrollTop = demoMessagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Small delay to ensure DOM is updated before scrolling
    const timeoutId = setTimeout(() => {
      scrollDemoToBottom();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [demoMessages]);

  // Handle click outside demo suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (demoSuggestionsRef.current && !demoSuggestionsRef.current.contains(event.target)) {
        setShowDemoSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDemoSuggestionClick = (suggestion) => {
    setDemoInputMessage(suggestion.text);
    setShowDemoSuggestions(false);
  };

  const sendDemoMessage = async () => {
    if (!demoInputMessage.trim() || isProcessingDemo) return;

    const chatId = demoActiveChatId;
    const userMessage = {
      id: Date.now(),
      content: demoInputMessage,
      role: 'user'
    };

    setDemoChatMessages(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), userMessage]
    }));
    const queryToSend = demoInputMessage;
    setDemoInputMessage('');
    setIsProcessingDemo(true);

    // Add processing placeholder
    const placeholderId = Date.now() + 1;
    setDemoChatMessages(prev => ({
      ...prev,
      [chatId]: [...(prev[chatId] || []), {
        id: placeholderId,
        content: '🔍 Analysing...',
        role: 'assistant',
        isPlaceholder: true
      }]
    }));

    // Simulate processing time
    setTimeout(() => {
      const response = findDemoResponse(queryToSend);
      
      setDemoChatMessages(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || []).map(msg => 
          msg.id === placeholderId 
            ? { ...msg, content: response.response, isPlaceholder: false }
            : msg
        )
      }));
      setIsProcessingDemo(false);
    }, 1500 + Math.random() * 1000);
  };

  const handleDemoKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendDemoMessage();
    }
  };

  const getRandomDemoSuggestions = (count = 5) => {
    const shuffled = [...demoSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Initialize suggestions when demo section opens (only once)
  useEffect(() => {
    if (showDemoSection && !demoInitializedRef.current) {
      demoInitializedRef.current = true;
      setQuickActionSuggestions(getRandomDemoSuggestions(3));
      setDropdownSuggestions(getRandomDemoSuggestions(5));
    }
    // Reset initialization flag when demo closes
    if (!showDemoSection) {
      demoInitializedRef.current = false;
    }
  }, [showDemoSection]);

  const refreshDropdownSuggestions = () => {
    setDropdownSuggestions(getRandomDemoSuggestions(5));
  };

  return (
    <div className="home-container">
      <header className={`home-header ${isHeaderScrolled ? 'scrolled' : ''}`}>
        <Logo size="small" showText={false} className="home-logo" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          <Link to="/login" className="login-link">Login</Link>
          <Link to="/register" className="register-link">Register</Link>
        </nav>
      </header>

      <main className="home-main">
        {/* Hero Section */}
        <section className="hero-section">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline 
            preload="auto"
            fetchpriority="high"
            className="hero-background-video"
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
          <div className="hero-overlay">
            <AsciiAnimation />
          </div>
          <div className="hero-content">
            <h1>
              Unified Security Research Platform<br />
              <span className="hero-subtitle">Powered by AI</span>
            </h1>
            <p>
              Sign in for connected endpoint research experience.
            </p>
            <div className="os-support-badges">
              <span>Supported Platforms:</span>
              <div className="os-icons">
                <FaWindows title="Windows" />
                <FaLinux title="Linux" />
                <FaApple title="macOS" />
              </div>
            </div>
            <div className="hero-buttons">
              <Link to="/register" className="btn btn-primary">
                Get Started <FaArrowRight />
              </Link>
              <button onClick={handleTryDemo} className="btn btn-secondary">
                Try Demo
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="terminal-mockup">
              <div className="terminal-header">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
                <span className="terminal-title">triagz@endpoint ~ $</span>
              </div>
              <div className="terminal-body" ref={terminalBodyRef}>
                {terminalLines.map((line, index) => (
                  <p key={index} className={line.type}>
                    {line.type === 'command' && <span className="prompt">$</span>} {line.text}
                  </p>
                ))}
                {currentCommand && (
                  <p className="command">
                    <span className="prompt">$</span> {currentCommand}
                    <span className="cursor">_</span>
                  </p>
                )}
                {!currentCommand && !isTyping && terminalLines.length > 0 && (
                  <p className="command">
                    <span className="prompt">$</span> <span className="cursor">_</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Demo Portal Section */}
        {showDemoSection && (
          <section className="demo-section" ref={demoSectionRef}>
            <div className="demo-header">
              <h2>Experience triagz</h2>
              <button 
                className="demo-close-btn"
                onClick={() => setShowDemoSection(false)}
                title="Close Demo"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="demo-portal">
              {/* Demo Sidebar */}
              <div className="demo-sidebar">
                <button className="demo-new-chat-btn">
                  <FaPlus />
                  <span>New Chat</span>
                </button>
                
                <div className="demo-chat-list">
                  {demoChatSessions.map(chat => {
                    const chatIcon = chat.icon === 'malware' ? <FaBug /> : 
                                     chat.icon === 'system' ? <FaDesktop /> : 
                                     chat.icon === 'network' ? <FaNetworkWired /> : <FaComments />;
                    const hasHistory = (demoChatMessages[chat.id] || []).length > 0;
                    return (
                      <div 
                        key={chat.id}
                        className={`demo-chat-session ${demoActiveChatId === chat.id ? 'active' : ''}`}
                        onClick={() => setDemoActiveChatId(chat.id)}
                      >
                        {chatIcon}
                        <span>{chat.name}</span>
                        {hasHistory && <span className="chat-badge">{(demoChatMessages[chat.id] || []).length}</span>}
                      </div>
                    );
                  })}
                </div>
                
                <div className="demo-profile-badge">
                  <FaUser />
                  <span>Demo User</span>
                </div>
              </div>
              
              {/* Demo Chat Area */}
              <div className="demo-chat-area">
                {/* Demo Navbar */}
                <div className="demo-navbar">
                  <div className="demo-navbar-left">
                    <span className="demo-badge">Demo Mode</span>
                    <div className="demo-suggestions-wrapper" ref={demoSuggestionsRef}>
                      <button 
                        className="demo-nav-btn"
                        onClick={() => setShowDemoSuggestions(!showDemoSuggestions)}
                      >
                        <FaLightbulb /> Suggestions
                      </button>
                      
                      {showDemoSuggestions && (
                        <div className="demo-suggestions-dropdown">
                          <div className="demo-suggestions-header">
                            <span>Query Suggestions</span>
                            <button 
                              className="demo-reload-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                refreshDropdownSuggestions();
                              }}
                            >
                              <FaRedo />
                            </button>
                          </div>
                          <div className="demo-suggestions-list">
                            {dropdownSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                className="demo-suggestion-item"
                                onClick={() => handleDemoSuggestionClick(suggestion)}
                              >
                                {suggestion.text}
                                <span className="demo-suggestion-category">{suggestion.category}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="demo-navbar-right">
                    <Link to="/register" className="demo-cta-btn">
                      Sign up for full access <FaArrowRight />
                    </Link>
                  </div>
                </div>
                
                {/* Demo Messages */}
                <div className="demo-messages" ref={demoMessagesContainerRef}>
                  {demoMessages.length === 0 ? (
                    <div className="demo-welcome">
                      <div className="demo-welcome-icon">
                        <FaTerminal />
                      </div>
                      <h3>Welcome to triagz Demo</h3>
                      <p>Ask any security research question to see the platform in action.</p>
                      <div className="demo-quick-actions">
                        {quickActionSuggestions.map((suggestion, index) => (
                          <button
                            key={`quick-${index}-${suggestion.text.substring(0, 10)}`}
                            className="demo-quick-action"
                            onClick={() => {
                              setDemoInputMessage(suggestion.text);
                            }}
                          >
                            {suggestion.text.length > 50 
                              ? suggestion.text.substring(0, 50) + '...' 
                              : suggestion.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    demoMessages.map((message) => (
                      <div 
                        key={message.id} 
                        className={`demo-message ${message.role} ${message.isPlaceholder ? 'placeholder' : ''}`}
                      >
                        <div className="demo-message-content">
                          <div className="demo-message-wrapper">
                            {message.isPlaceholder ? (
                              <div className="demo-message-text demo-loading">
                                <div className="demo-loading-animation">
                                  <div className="demo-loading-dots">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                  </div>
                                  <span className="demo-loading-text">Analysing...</span>
                                </div>
                              </div>
                            ) : (
                              <div 
                                className="demo-message-text"
                                dangerouslySetInnerHTML={{ __html: message.content }}
                              />
                            )}
                            {message.role === 'assistant' && !message.isPlaceholder && (
                              <div className="demo-message-actions">
                                <button className="demo-action-btn" title="Copy">
                                  <FaCopy />
                                </button>
                                <button className="demo-action-btn" title="Like">
                                  <FaThumbsUp />
                                </button>
                                <button className="demo-action-btn" title="Dislike">
                                  <FaThumbsDown />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={demoMessagesEndRef}></div>
                </div>
                
                {/* Demo Input */}
                <div className="demo-input-container">
                  <div className="demo-input">
                    <textarea
                      placeholder="Type a security query (e.g., 'list all running processes')"
                      value={demoInputMessage}
                      onChange={(e) => setDemoInputMessage(e.target.value)}
                      onKeyDown={handleDemoKeyDown}
                      disabled={isProcessingDemo}
                    />
                    <button 
                      onClick={sendDemoMessage} 
                      disabled={!demoInputMessage.trim() || isProcessingDemo}
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Quick Start Section */}
        <section className="quick-start-section">
          <div className="container">
            <Suspense fallback={<div className="animation-placeholder" />}>
              <ProductFlowAnimation />
            </Suspense>
          </div>
        </section>

        {/* Features Section - Redesigned */}
        <section className="features-section-v2">
          <div className="container">
            <div className="features-header-v2">
              <h2>Security Research Without Limits</h2>
              <p className="features-subtitle">
                triagz eliminates tool complexity—perform deep endpoint analysis with natural language across any platform
              </p>
            </div>
            
            <div className="bento-grid">
              {/* Large Feature Card - Malware Analysis */}
              <div className="bento-card bento-large" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content">
                    <div className="bento-icon-wrap">
                      <FaShieldAlt />
                      <div className="icon-ring"></div>
                    </div>
                    <div className="bento-text">
                      <h3>Advanced Malware Analysis</h3>
                      <p>Analyze thousands of malware samples with natural language queries. Identify patterns, extract IOCs, and understand threats without writing a single line of code.</p>
                    </div>
                    <div className="bento-visual">
                      <div className="code-preview">
                        <span className="code-prompt">›</span>
                        <span className="code-text">analyze PE imports for dolphine.exe</span>
                        <span className="code-cursor"></span>
                      </div>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>

              {/* Medium Card - Multi-Platform */}
              <div className="bento-card bento-medium" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content">
                    <div className="bento-icon-wrap accent-blue">
                      <FaTerminal />
                      <div className="icon-ring"></div>
                    </div>
                    <h3>Multi-Platform Support</h3>
                    <p>Execute security tasks across Windows, Linux, and macOS with simple prompts.</p>
                    <div className="platform-icons">
                      <span className="platform-badge"><FaWindows /></span>
                      <span className="platform-badge"><FaLinux /></span>
                      <span className="platform-badge"><FaApple /></span>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>

              {/* Small Card - Real-time */}
              <div className="bento-card bento-small" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content">
                    <div className="bento-icon-wrap accent-green">
                      <FaBolt />
                      <div className="icon-ring"></div>
                    </div>
                    <h3>Real-time Analysis</h3>
                    <p>Instant AI-powered results</p>
                    <div className="speed-indicator">
                      <div className="speed-bar"></div>
                      <span>{"<"}2s response</span>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>

              {/* Medium Card - Integrations */}
              <div className="bento-card bento-medium" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content">
                    <div className="bento-icon-wrap accent-orange">
                      <FaCode />
                      <div className="icon-ring"></div>
                    </div>
                    <h3>Infinite Integrations</h3>
                    <p>Built-in support for VirusTotal, AbuseIPDB, and more—no tool hopping.</p>
                    <div className="integration-logos">
                      <div className="integration-dot"></div>
                      <div className="integration-dot"></div>
                      <div className="integration-dot"></div>
                      <span>+20 integrations</span>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>

              {/* Large Card - Agent Management */}
              <div className="bento-card bento-wide" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content bento-content-horizontal">
                    <div className="bento-icon-wrap accent-purple">
                      <FaLock />
                      <div className="icon-ring"></div>
                    </div>
                    <div className="bento-text">
                      <h3>Centralized Agent Management</h3>
                      <p>Create and manage multiple agents across different platforms with separate profiles for each security project. Full control, zero complexity.</p>
                    </div>
                    <div className="agent-preview">
                      <div className="agent-card-mini active">
                        <FaWindows /> Agent-01
                      </div>
                      <div className="agent-card-mini">
                        <FaLinux /> Agent-02
                      </div>
                      <div className="agent-card-mini">
                        <FaApple /> Agent-03
                      </div>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>

              {/* Small Card - Scalable */}
              <div className="bento-card bento-small" data-tilt>
                <div className="bento-card-inner">
                  <div className="bento-glow"></div>
                  <div className="bento-content">
                    <div className="bento-icon-wrap accent-cyan">
                      <FaCloud />
                      <div className="icon-ring"></div>
                    </div>
                    <h3>Scalable</h3>
                    <p>Plug & play architecture</p>
                    <div className="scale-visual">
                      <div className="scale-dot"></div>
                      <div className="scale-dot"></div>
                      <div className="scale-dot"></div>
                      <div className="scale-line"></div>
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Installation Video Section */}
        <section className="installation-video-section">
          <div className="container">
            <div className="video-section-content">
              <div className="video-section-header">
                <h2>Agentic endpoint under <span className="highlight-text">1 minute</span></h2>
                <p className="video-section-subtitle">
                  Watch how easy it is to set up triagz and start your connected security research journey
                </p>
              </div>
              
              <div className="video-container">
                <div className="video-wrapper">
                  <div className="video-glow"></div>
                  <video 
                    controls 
                    playsInline
                    poster=""
                    className="installation-video"
                  >
                    <source src="https://downloads.triagz.com/landing-page/triagz%20installation%20final.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="video-decoration">
                  <div className="decoration-dot"></div>
                  <div className="decoration-dot"></div>
                  <div className="decoration-dot"></div>
                </div>
              </div>
              
              <div className="video-cta">
                <Link to="/downloads" className="btn btn-primary">
                  <FaDownload /> Download Now
                </Link>
                <Link to="/register" className="btn btn-secondary">
                  Get Started <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section - Redesigned */}
        <section className="use-cases-section-v2">
          <div className="container">
            <div className="use-cases-header">
              <h2>Trusted by Security Teams Worldwide</h2>
              <p className="use-cases-subtitle">Empowering professionals across every security discipline</p>
            </div>
            
            <div className="use-cases-grid-v2">
              <div className="use-case-card">
                <div className="use-case-card-inner">
                  <div className="use-case-number">01</div>
                  <div className="use-case-icon-wrap">
                    <FaUsers />
                  </div>
                  <div className="use-case-content">
                    <h3>Security Researchers</h3>
                    <p>Accelerate malware analysis and threat hunting with natural language queries across any endpoint.</p>
                  </div>
                  <div className="use-case-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </div>

              <div className="use-case-card">
                <div className="use-case-card-inner">
                  <div className="use-case-number">02</div>
                  <div className="use-case-icon-wrap">
                    <FaShieldAlt />
                  </div>
                  <div className="use-case-content">
                    <h3>Incident Response Teams</h3>
                    <p>Rapid forensic analysis, evidence collection and comprehensive system investigation.</p>
                  </div>
                  <div className="use-case-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </div>

              <div className="use-case-card">
                <div className="use-case-card-inner">
                  <div className="use-case-number">03</div>
                  <div className="use-case-icon-wrap">
                    <FaChartLine />
                  </div>
                  <div className="use-case-content">
                    <h3>Threat Intelligence</h3>
                    <p>IOC extraction and pattern analysis with top threat intelligence platform integrations.</p>
                  </div>
                  <div className="use-case-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </div>

              <div className="use-case-card">
                <div className="use-case-card-inner">
                  <div className="use-case-number">04</div>
                  <div className="use-case-icon-wrap">
                    <FaGlobe />
                  </div>
                  <div className="use-case-content">
                    <h3>Enterprise Security</h3>
                    <p>Large-scale connected endpoint analysis and centralized security administration.</p>
                  </div>
                  <div className="use-case-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Security Domains Section - Mistral-style */}
        <section className="security-domains-section">
          <div className="container">
            <div className="security-domains-header">
              <h2>What Triagz Can Do For You</h2>
              <p className="security-domains-subtitle">
                One platform for every security domain—powered by AI and built for researchers
              </p>
            </div>

            <div className="security-domains-content">
              {/* Left Sidebar Tabs */}
              <div className="security-domains-tabs">
                {Object.values(securityDomains).map((domain) => {
                  const IconComponent = domain.icon;
                  return (
                    <button
                      key={domain.id}
                      className={`security-domain-tab ${activeSecurityDomain === domain.id ? 'active' : ''}`}
                      onClick={() => setActiveSecurityDomain(domain.id)}
                    >
                      <IconComponent className="tab-icon" />
                      <span>{domain.title}</span>
                      <FaArrowRight className="tab-arrow" />
                    </button>
                  );
                })}
              </div>

              {/* Middle Description & Right Video */}
              <div className="security-domains-display">
                <div className="security-domain-description">
                  <p>{securityDomains[activeSecurityDomain].description}</p>
                </div>

                <div className="security-domain-video">
                  <div className="video-player-wrapper">
                    <video 
                      key={activeSecurityDomain}
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="domain-video"
                    >
                      <source src={securityDomains[activeSecurityDomain].videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div className="video-overlay-gradient"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section - Redesigned */}
        <section className="why-choose-section-v2">
          <div className="container">
            <div className="why-choose-header">
              <h2>Triagz Experience</h2>
              <p className="why-choose-subtitle">Built for security researchers who demand excellence</p>
            </div>

            <div className="why-choose-layout">
              {/* Benefits Grid */}
              <div className="benefits-grid-v2">
                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaRocket />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>Plug & Play</h4>
                    <p>Works within your existing infrastructure without disruption</p>
                  </div>
                </div>

                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaBolt />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>AI-Powered Analysis</h4>
                    <p>Highly accurate LLM for intelligent security insights</p>
                  </div>
                </div>

                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaGlobe />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>Cross-Platform</h4>
                    <p>Windows, Linux, and macOS agent support</p>
                  </div>
                </div>

                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaChartLine />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>Real-Time Results</h4>
                    <p>Instant analysis with full history tracking</p>
                  </div>
                </div>

                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaShieldAlt />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>Enterprise Security</h4>
                    <p>Grade-A security standards and compliance</p>
                  </div>
                </div>

                <div className="benefit-card-v2">
                  <div className="benefit-icon-v2">
                    <FaCode />
                  </div>
                  <div className="benefit-text-v2">
                    <h4>Extensive API</h4>
                    <p>Full integration support for your workflows</p>
                  </div>
                </div>
              </div>

              {/* Stats Panel */}
              <div className="stats-panel-v2">
                <div className="stats-panel-inner">
                  <div className="stat-item-v2">
                    <div className="stat-number-v2">100+</div>
                    <div className="stat-label-v2">Actions Supported</div>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{ width: '85%' }}></div>
                    </div>
                  </div>

                  <div className="stat-item-v2">
                    <div className="stat-number-v2">3</div>
                    <div className="stat-label-v2">Platforms</div>
                    <div className="stat-platforms">
                      <span><FaWindows /></span>
                      <span><FaLinux /></span>
                      <span><FaApple /></span>
                    </div>
                  </div>

                  <div className="stat-item-v2">
                    <div className="stat-number-v2">99.9%</div>
                    <div className="stat-label-v2">Uptime Guarantee</div>
                    <div className="stat-bar">
                      <div className="stat-bar-fill full" style={{ width: '99.9%' }}></div>
                    </div>
                  </div>

                  <div className="stat-item-v2">
                    <div className="stat-number-v2">{"<"} 60s</div>
                    <div className="stat-label-v2">Avg Response Time</div>
                    <div className="stat-bar">
                      <div className="stat-bar-fill fast" style={{ width: '95%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Suspense fallback={null}>
            <VirusAnimation />
          </Suspense>
          <div className="container relative-z">
            <div className="cta-content">
              <h2>Ready to Transform Your Security Research experince?</h2>
              <p>
                Join this no jargons, plug and play platform 
                for advanced endpoint threat analysis and research needs.
              </p>
              <div className="cta-buttons">
                <Link to="/register" className="btn btn-primary btn-large">
                  Start Your Research <FaArrowRight />
                </Link>
                <Link to="/downloads" className="btn btn-secondary btn-large">
                  <FaDownload /> Download Now
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Creative Terminal-Style Footer */}
      <footer className="footer-v2">
        <div className="footer-terminal">
          {/* Terminal Header */}
          <div className="terminal-header-v2">
            <div className="terminal-dots">
              <span className="dot red"></span>
              <span className="dot yellow"></span>
              <span className="dot green"></span>
            </div>
            <span className="terminal-title">triagz@security:~</span>
            <div className="terminal-actions">
              <span className="status-badge">
                <span className="status-dot"></span>
                System Online
              </span>
            </div>
          </div>

          {/* Terminal Content */}
          <div className="terminal-content-v2">
            {/* Command Output Section */}
            <div className="footer-grid-v2">
              {/* Left Panel - About */}
              <div className="footer-panel main-panel">
                <div className="command-line">
                  <span className="prompt">$</span>
                  <span className="command">cat /about/triagz.md</span>
                </div>
                <div className="output-block">
                  <div className="footer-brand">
                    <Logo 
                      size="medium" 
                      showText={false} 
                      isClickable={false} 
                      className="footer-logo-v2" 
                      useFullLogo={true}
                    />
                  </div>
                  <p className="footer-desc">
                    Natural language based endpoint research platform. 
                  </p>
                  <div className="social-bar">
                    <a href="https://x.com/triagzlab" target="_blank" rel="noopener noreferrer" className="social-chip">
                      <FaTwitter /> <span>@triagzlab</span>
                    </a>
                    <a href="https://www.linkedin.com/company/triagz" target="_blank" rel="noopener noreferrer" className="social-chip">
                      <FaLinkedin /> <span>LinkedIn</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Navigation Panels */}
              <div className="footer-panel nav-panel">
                <div className="command-line">
                  <span className="prompt">$</span>
                  <span className="command">ls ./product/</span>
                </div>
                <div className="output-block">
                  <ul className="nav-list">
                    <li>
                      <Link to="/downloads">
                        <span className="file-icon">📦</span>
                        <span className="file-name">agents</span>
                        <span className="file-type">.download</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/subscription">
                        <span className="file-icon">💎</span>
                        <span className="file-name">plans</span>
                        <span className="file-type">.pricing</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="footer-panel nav-panel">
                <div className="command-line">
                  <span className="prompt">$</span>
                  <span className="command">ls ./resources/</span>
                </div>
                <div className="output-block">
                  <ul className="nav-list">
                    <li>
                      <Link to="/blog">
                        <span className="file-icon">📝</span>
                        <span className="file-name">blog</span>
                        <span className="file-type">.posts</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/blog/7">
                        <span className="file-icon">📚</span>
                        <span className="file-name">docs</span>
                        <span className="file-type">.wiki</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/report-bug">
                        <span className="file-icon">🐛</span>
                        <span className="file-name">bugs</span>
                        <span className="file-type">.report</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/add-suggestions">
                        <span className="file-icon">💡</span>
                        <span className="file-name">ideas</span>
                        <span className="file-type">.suggest</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="footer-panel nav-panel">
                <div className="command-line">
                  <span className="prompt">$</span>
                  <span className="command">ls ./company/</span>
                </div>
                <div className="output-block">
                  <ul className="nav-list">
                    <li>
                      <Link to="/our-vision">
                        <span className="file-icon">🎯</span>
                        <span className="file-name">vision</span>
                        <span className="file-type">.about</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/career">
                        <span className="file-icon">🚀</span>
                        <span className="file-name">careers</span>
                        <span className="file-type">.jobs</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/contact">
                        <span className="file-icon">✉️</span>
                        <span className="file-name">contact</span>
                        <span className="file-type">.us</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/donation">
                        <span className="file-icon">❤️</span>
                        <span className="file-name">support</span>
                        <span className="file-type">.donate</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer Bottom - Status Bar */}
            <div className="footer-status-bar">
              <div className="status-left">
                <span className="status-item">
                  <span className="prompt">©</span> 2025 triagz labs
                </span>
                <span className="status-divider">|</span>
                <span className="status-item clickable">
                  <Link to="/privacy-policy">privacy.policy</Link>
                </span>
                <span className="status-divider">|</span>
                <span className="status-item clickable">
                  <Link to="/terms-of-service">terms.service</Link>
                </span>
                <span className="status-divider">|</span>
                <button 
                  onClick={() => setShowCookieModal(true)} 
                  className="status-item clickable cookie-btn"
                >
                  cookies.manage
                </button>
              </div>
              <div className="status-right">
                <span className="version-tag">v1.0.1</span>
                <span className="uptime-badge">
                  <span className="pulse-dot"></span>
                  99.9% uptime
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Management Modal */}
      {showCookieModal && (
        <div className="cookie-modal-overlay">
          <div className="cookie-modal">
            <div className="cookie-modal-header">
              <div className="cookie-modal-title">
                <FaCookieBite className="cookie-icon" />
                <h3>Manage Cookies</h3>
              </div>
              <button 
                onClick={() => setShowCookieModal(false)}
                className="cookie-close-btn"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="cookie-modal-content">
              <p className="cookie-description">
                We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                You can manage your preferences below.
              </p>
              
              <div className="cookie-categories">
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <div className="cookie-category-info">
                      <h4>Necessary Cookies</h4>
                      <p>Essential for the website to function properly. Cannot be disabled.</p>
                    </div>
                    <div className="cookie-toggle disabled">
                      <FaToggleOn />
                    </div>
                  </div>
                </div>
                
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <div className="cookie-category-info">
                      <h4>Analytics Cookies</h4>
                      <p>Help us understand how visitors interact with our website.</p>
                    </div>
                    <button 
                      className="cookie-toggle"
                      onClick={() => handleCookieToggle('analytics')}
                    >
                      {cookieSettings.analytics ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                </div>
                
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <div className="cookie-category-info">
                      <h4>Functional Cookies</h4>
                      <p>Enable enhanced functionality and personalization.</p>
                    </div>
                    <button 
                      className="cookie-toggle"
                      onClick={() => handleCookieToggle('functional')}
                    >
                      {cookieSettings.functional ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                </div>
                
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <div className="cookie-category-info">
                      <h4>Advertising Cookies</h4>
                      <p>Used to deliver relevant advertisements and track ad performance.</p>
                    </div>
                    <button 
                      className="cookie-toggle"
                      onClick={() => handleCookieToggle('advertising')}
                    >
                      {cookieSettings.advertising ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="cookie-modal-footer">
              <button 
                onClick={handleSaveCookies}
                className="btn btn-secondary cookie-save-btn"
              >
                Save Preferences
              </button>
              <button 
                onClick={handleAcceptAll}
                className="btn btn-primary cookie-accept-btn"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home; 